import '@livekit/components-styles'
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined'
import { MediaDeviceFailure } from 'livekit-client'
import { useCallback, useEffect, useState } from 'react'
import { useCookies } from 'react-cookie'
import uuid from 'react-uuid'
import toast from '../components/Toast'
import { Layout } from '../pages/landing'
import api from '../services/api'
import { AudioNode, ImageNode, RedirectNode, SilentNode, VideoNode } from '../types/nodeTypes'
import End from './nodes/End'
import TermsModal from './TermsModal'
//import TimerComponent from './TimerComponent'
import VocadsAssistant from './VocadsAssistant'
import wordings from '../wording.json'

const audioInSound = require('../audios/audio-in.mp3')
const audioOutSound = require('../audios/audio-out.mp3')

export type ConnectionDetails = {
  serverUrl: string
  roomName: string
  participantName: string
  participantToken: string
  conversationId: string
}

export type DataMessageType =
  | 'segment'
  | 'committed'
  | 'init'
  | 'ready'
  | 'user_segment' //
  | 'user_committed'
  | 'options'
  | 'response'
  | 'selected'
  | 'audio'
  | 'video'
  | 'image'
  | 'silent'
  | 'error'
  | 'redirect'
  | 'end'
  | 'open_mic'
  | 'close_mic'

export interface DataMessage {
  type: DataMessageType
  content:
    | string
    | Record<string, unknown>
    | string[]
    | number
    | SilentNode
    | VideoNode
    | RedirectNode
    | AudioNode
    | ImageNode
    | undefined
}

export type SupportedLanguage = keyof typeof wordings

export default function VoiceRoom({
  activeLayout,
  language,
  primaryColor,
  secondaryColor
}: {
  activeLayout: Layout
  language: string
  primaryColor: string
  secondaryColor: string
}): JSX.Element {
  const [connectionDetails, updateConnectionDetails] = useState<ConnectionDetails | undefined>(
    undefined
  )

  const searchParams = new URLSearchParams(window.location.search)
  const preview = searchParams.has('widget-preview')

  const [cookie, setCookie] = useCookies(['visitorId'])
  const [endOpen, setEndOpenned] = useState(false)
  const [initDisable, setInitDisable] = useState(false)
  useEffect(() => {
    let visitorId = (cookie as { visitorId?: string | null }).visitorId

    if (visitorId === null || visitorId === undefined) {
      visitorId = uuid()
      setCookie('visitorId', visitorId, { path: '/', maxAge: 60 * 60 * 24 * 365 }) // Set cookie for 1 year
    }
  }, [cookie, setCookie])

  const audioIn = new Audio(audioInSound)
  const audioOut = new Audio(audioOutSound)

  const onConnectButtonClicked = useCallback(async (): Promise<void> => {
    try {
      setInitDisable(true)
      const campaignId = searchParams.get('id')
      if (campaignId !== null) {
        const thirdpartyId = searchParams.get('v_ref') ?? undefined
        const stt = searchParams.get('stt') ?? undefined
        const tts = searchParams.get('tts') ?? undefined
        const connectionDetailsData = await api.init(
          campaignId,
          (cookie as { visitorId: string }).visitorId,
          preview,
          thirdpartyId,
          stt,
          tts
        )
        updateConnectionDetails(connectionDetailsData)
        setInitDisable(false)

        // play start sound
        audioIn.volume = 0.3
        audioIn.loop = false
        audioIn.currentTime = 0
        audioIn.play()
      } else {
        console.error('campaignId  is null')
        toast.error('No campaignId found.')
        setInitDisable(false)
      }
    } catch (error) {
      toast.error('Failed to connect.')
      console.error('Failed to connect:', error)
      setInitDisable(false)
    }
  }, [cookie])

  const handleEndOpen = (): void => {
    setEndOpenned(!endOpen)
  }

  const [hasAgreeTerm, setHasConsentTerm] = useState(
    localStorage.getItem('vocads-term-and-condition') === 'agree'
  )

  const handleAcceptTerm = (): void => {
    if (!hasAgreeTerm) {
      setHasConsentTerm(true)
      localStorage.setItem('vocads-term-and-condition', 'agree')
    } else {
      setHasConsentTerm(false)
      localStorage.setItem('vocads-term-and-condition', 'disagree')
    }
  }

  const renderWording = (language: string): JSX.Element => {
    const lang: SupportedLanguage = (
      language in wordings ? language : 'default'
    ) as SupportedLanguage
    const content = wordings[lang]

    return (
      <div className="start-button-text">
        <span className="click-to-start-text">{content.clickToStart}</span>
        <p className="term-text">
          {content.terms} <br />
          <>
            <a
              href={content.termsLink}
              target="_blank"
              rel="noopener noreferrer"
              className="term-link"
            >
              {content.termsLinkText}
            </a>{' '}
            and{' '}
            <a
              href={content.privacyLink}
              target="_blank"
              rel="noopener noreferrer"
              className="term-link"
            >
              {content.privacyLinkText}
            </a>
          </>
        </p>
      </div>
    )
  }

  return (
    <main data-lk-theme="default" className="start-button-page">
      {hasAgreeTerm && <TermsModal handleAcceptTerm={handleAcceptTerm} />}
      {/* {preview === true && (
        <TimerComponent
          start={connectionDetails !== undefined}
          stop={connectionDetails === undefined}
        />
      )} */}
      {connectionDetails !== undefined ? (
        <VocadsAssistant
          handleEndOpen={handleEndOpen}
          activeLayout={activeLayout}
          token={connectionDetails?.participantToken}
          serverUrl={connectionDetails?.serverUrl}
          connect={connectionDetails !== undefined}
          audio={true}
          video={false}
          onMediaDeviceFailure={onDeviceFailure}
          onDisconnected={() => {
            updateConnectionDetails(undefined)
          }}
          onConnected={() => {
            // wait until audiIn end before playing audioOut
            setTimeout(() => {
              audioOut.volume = 0.3
              audioOut.currentTime = 0
              audioOut.play()
            }, audioIn.duration * 1000)
          }}
          onError={(error: Error) => {
            console.log(`errror`)
            console.log(error)
          }}
          className=""
          conversationId={connectionDetails?.conversationId}
          isConnected={connectionDetails !== undefined}
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
        />
      ) : endOpen ? (
        <End lang={language} />
      ) : (
        <div className="start-button-container">
          <button
            disabled={initDisable}
            style={
              {
                '--primary-color': primaryColor,
                '--secondary-color': secondaryColor
              } as React.CSSProperties
            }
            className={initDisable ? 'start-disabled' : 'start-button'}
            onClick={onConnectButtonClicked}
          >
            <PlayArrowOutlinedIcon style={{ fontSize: '60px' }} />
          </button>
          {renderWording(language)}
        </div>
      )}
    </main>
  )
}

function onDeviceFailure(error?: MediaDeviceFailure): void {
  console.error(error)
  alert(
    'Error acquiring camera or microphone permissions. Please make sure you grant the necessary permissions in your browser and reload the tab'
  )
}
